
import { useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import PageLayout from "@/components/layout/PageLayout";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Card, CardContent } from "@/components/ui/card";
import { DocumentUpload } from "@/components/offer-management/DocumentUpload";
import { DocumentLibrary } from "@/components/offer-management/DocumentLibrary";
import { TeamManagement } from "@/components/offer-management/TeamManagement";
import { NoTenderSelected } from "@/components/offer-management/NoTenderSelected";
import { TaskManagement } from "@/components/offer-management/TaskManagement";
import { TenderHeader } from "@/components/offer-management/TenderHeader";
import { TemplatesTab } from "@/components/offer-management/TemplatesTab";
import { HistoryTab } from "@/components/offer-management/HistoryTab";
import { TranslatedDocuments } from "@/components/offer-management/TranslatedDocuments";
import { useState, useEffect } from "react";
import { TenderData } from "@/types/tender";
import { useTranslation } from "react-i18next";

interface DocumentType {
  id: string;
  file_name: string;
  file_type: string;
  file_size: number;
  created_at: string;
  file_path: string;
  description?: string;
}

interface TeamMemberRole {
  role: string | null;
}

interface TranslationCheck {
  id: string;
}

interface TranslationProcessingCheck {
  id: string;
  status: string;
}

const OfferManagement = () => {
  const { tenderId } = useParams<{ tenderId: string }>();
  const { toast } = useToast();
  const [searchQuery, setSearchQuery] = useState("");
  const { t } = useTranslation('offerManagement');

  useEffect(() => {
    if (tenderId) {
      const updateTenderStatus = async () => {
        try {
          const { data: tender, error: fetchError } = await supabase
            .from('saved_tenders')
            .select('status')
            .eq('id', tenderId)
            .single();
            
          if (fetchError) {
            console.error("Error fetching tender status:", fetchError);
            return;
          }
          
          if (tender?.status === 'not_started') {
            const { error: updateError } = await supabase
              .from('saved_tenders')
              .update({ status: 'in_process' })
              .eq('id', tenderId);
              
            if (updateError) {
              console.error("Error updating tender status:", updateError);
              return;
            }
            
            console.log("Tender status updated to 'in_process'");
          }
        } catch (error) {
          console.error("Error in status update:", error);
        }
      };
      
      updateTenderStatus();
    }
  }, [tenderId]);

  useEffect(() => {
    if (tenderId) {
      const checkTranslationsOnLoad = async () => {
        try {
          const { data, error } = await supabase
            .from('translated_documents')
            .select('id, status')
            .eq('tender_id', tenderId)
            .eq('status', 'processing');
            
          if (error) {
            console.error("Error checking for processing translations:", error);
            return;
          }
          
          if (data && data.length > 0) {
            console.log(`Found ${data.length} documents in processing status on page load, triggering status check`);
            await supabase.functions.invoke("check-translation-status");
          }
        } catch (error) {
          console.error("Error checking translations on page load:", error);
        }
      };
      
      checkTranslationsOnLoad();
    }
  }, [tenderId]);

  const { data: tender, refetch: refetchTender } = useQuery<TenderData | null>({
    queryKey: ['tender', tenderId],
    queryFn: async () => {
      if (!tenderId) return null;

      const { data, error } = await supabase
        .from('saved_tenders')
        .select('*')
        .eq('id', tenderId)
        .maybeSingle();

      if (error) throw error;
      return data;
    },
    enabled: Boolean(tenderId)
  });

  const { data: userRole } = useQuery<string | null>({
    queryKey: ['user-role', tenderId],
    queryFn: async () => {
      if (!tenderId) return null;

      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return null;

      if (tender?.user_id === user.id) {
        return 'owner';
      }

      const { data: teamMember, error } = await supabase
        .from('tender_team_members')
        .select('role')
        .eq('tender_id', tenderId)
        .eq('user_id', user.id)
        .maybeSingle();

      if (error) throw error;
      return (teamMember as TeamMemberRole | null)?.role || null;
    },
    enabled: Boolean(tenderId && tender)
  });

  const { 
    data: documents = [], 
    isLoading: isLoadingDocuments, 
    refetch: refetchDocuments 
  } = useQuery<DocumentType[]>({
    queryKey: ['tender-documents', tenderId],
    queryFn: async () => {
      if (!tenderId) return [];

      const { data, error } = await supabase
        .from('tender_documents')
        .select('*')
        .eq('tender_id', tenderId)
        .order('created_at', { ascending: false });

      if (error) throw error;
      return data as DocumentType[] || [];
    },
    enabled: Boolean(tenderId)
  });

  const handleDownload = async (filePath: string, fileName: string) => {
    try {
      const { data, error } = await supabase.storage
        .from('tender_documents')
        .download(filePath);

      if (error) throw error;

      const url = URL.createObjectURL(data);
      const a = document.createElement('a');
      a.href = url;
      a.download = fileName;
      document.body.appendChild(a);
      a.click();
      URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (error) {
      console.error('Error downloading file:', error);
      toast({
        title: t("errorMessages.downloadFailed"),
        description: t("errorMessages.downloadFailedDesc"),
        variant: "destructive"
      });
    }
  };

  const handleDelete = async (id: string, filePath: string) => {
    try {
      const { data: translations, error: checkError } = await supabase
        .from('translated_documents')
        .select('id')
        .eq('tender_id', tenderId)
        .eq('original_document_id', id);
        
      if (checkError) {
        console.error('Error checking for translations:', checkError);
      } else if (translations && translations.length > 0) {
        toast({
          title: t("errorMessages.deleteFailed"),
          description: `This document has ${translations.length} translations. Please delete the translations first.`,
          variant: "destructive"
        });
        return;
      }
      
      const { error: storageError } = await supabase.storage
        .from('tender_documents')
        .remove([filePath]);

      if (storageError) {
        console.error('Storage removal error:', storageError);
      }

      const { error: dbError } = await supabase
        .from('tender_documents')
        .delete()
        .eq('id', id);

      if (dbError) throw dbError;

      refetchDocuments();

      toast({
        title: t("common.success"),
        description: t("documents.deleteSuccess"),
      });
    } catch (error) {
      console.error('Error deleting document:', error);
      toast({
        title: t("errorMessages.deleteFailed"),
        description: t("errorMessages.deleteFailedDesc"),
        variant: "destructive"
      });
    }
  };

  if (!tenderId) {
    return <NoTenderSelected />;
  }

  const tenderData = tender?.tender_data as { title?: string; description?: string };
  const filteredDocuments = Array.isArray(documents) 
    ? documents.filter(doc => 
        doc.file_name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        (doc.description?.toLowerCase() || '').includes(searchQuery.toLowerCase())
      )
    : [];

  const canEdit = userRole === 'owner' || userRole === 'editor';

  return (
    <PageLayout>
      <div className="container mx-auto px-4 py-8">
        <TenderHeader tenderId={tenderId} tenderData={tenderData} />

        <Tabs defaultValue="documents" className="space-y-6">
          <TabsList>
            <TabsTrigger value="documents">{t("tabs.documents")}</TabsTrigger>
            <TabsTrigger value="team">{t("tabs.team")}</TabsTrigger>
            <TabsTrigger value="tasks">{t("tabs.tasks")}</TabsTrigger>
            <TabsTrigger value="templates">{t("tabs.templates")}</TabsTrigger>
            <TabsTrigger value="history">{t("tabs.history")}</TabsTrigger>
          </TabsList>

          <TabsContent value="documents">
            <div className="grid gap-6">
              {canEdit && <DocumentUpload onUploadComplete={() => refetchDocuments()} />}
              <DocumentLibrary
                documents={filteredDocuments}
                isLoading={isLoadingDocuments}
                tenderId={tenderId}
                searchQuery={searchQuery}
                onSearchChange={setSearchQuery}
                onDownload={handleDownload}
                onDelete={canEdit ? handleDelete : undefined}
                onRefresh={refetchDocuments}
              />
              <TranslatedDocuments />
            </div>
          </TabsContent>

          <TabsContent value="team">
            <TeamManagement tenderId={tenderId} />
          </TabsContent>

          <TabsContent value="tasks">
            <Card>
              <CardContent className="pt-6">
                <TaskManagement tenderId={tenderId} />
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="templates">
            <TemplatesTab />
          </TabsContent>

          <TabsContent value="history">
            <HistoryTab />
          </TabsContent>
        </Tabs>
      </div>
    </PageLayout>
  );
};

export default OfferManagement;
